import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import GalleryCard from "@/components/feature/GalleryCard";
import EmptyState from "@/components/base/EmptyState";
import ActionNotice from "@/components/base/ActionNotice";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { galleries } from "@/mocks/galleries";
import { copyCurrentLink } from "@/utils/share";
import { formatDate } from "@/utils/format";

export default function ImageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const gallery = galleries.find((item) => item.id === id);
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [notice, setNotice] = useState("");
  const { isFavorite, toggle } = useFavorites("gallery");

  useEffect(() => {
    setIndex(0);
    setZoom(1);
  }, [id]);

  usePageMeta({
    title: gallery ? `${gallery.title} - ${gallery.theme}图集 - 成人网` : "图片详情 - 成人网",
    description: gallery
      ? `${gallery.title}，${gallery.theme}主题写真图集，摄影师 ${gallery.photographer}，共 ${gallery.photoCount} 张演示图片，支持大图缩放浏览。`
      : "成人网图片详情页，支持大图缩放、前后切换与收藏。",
    keywords: gallery
      ? `${gallery.title},${gallery.theme},写真图集,成人图片`
      : "成人图片,图片详情,成人网",
  });

  const recommended = useMemo(() => {
    if (!gallery) {
      return galleries.slice(0, 4);
    }
    return galleries.filter((item) => item.id !== gallery.id).slice(0, 4);
  }, [gallery]);

  if (!gallery) {
    return (
      <div className="container-page py-10">
        <EmptyState
          icon="ri-image-line"
          title="没有找到这组图集"
          description="链接可能已经失效，或者这组演示图集已被移动。可以回到图片分类继续浏览。"
          actionLabel="返回图片分类"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/image-category");
          }}
        />
      </div>
    );
  }

  const current = gallery.images[index] ?? gallery.cover;
  const favorited = isFavorite(gallery.id);

  const goPrev = () => {
    setIndex((prev) => (prev - 1 + gallery.images.length) % gallery.images.length);
    setZoom(1);
  };

  const goNext = () => {
    setIndex((prev) => (prev + 1) % gallery.images.length);
    setZoom(1);
  };

  const handleFavorite = () => {
    const added = toggle(gallery.id);
    setNotice(added ? "已加入收藏（仅保存在当前设备）" : "已取消收藏");
  };

  const handleShare = async () => {
    const ok = await copyCurrentLink();
    setNotice(ok ? "链接已复制，可以分享给朋友了" : "复制失败，请手动复制浏览器地址");
  };

  return (
    <div className="container-page py-7">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "图片分类", to: "/image-category" },
          { label: gallery.title },
        ]}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_318px]">
        <div className="min-w-0">
          <div className="rounded-lg border border-background-200 bg-background-100 p-3">
            <div className="relative flex h-[420px] items-center justify-center overflow-auto rounded-md bg-background-900 md:h-[560px]">
              <img
                src={current}
                alt={`${gallery.title} 第 ${index + 1} 张演示图片`}
                title={`${gallery.title} ${gallery.theme} 成人图片`}
                style={{ transform: `scale(${zoom})` }}
                className="max-h-full max-w-full object-contain transition-transform duration-300"
              />

              <button
                type="button"
                onClick={goPrev}
                className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/60 text-foreground-800 transition-colors hover:bg-primary-500 hover:text-background-50 cursor-pointer"
                aria-label="上一张"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/60 text-foreground-800 transition-colors hover:bg-primary-500 hover:text-background-50 cursor-pointer"
                aria-label="下一张"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>

              <span className="absolute bottom-3 left-3 rounded bg-background-950/70 px-2 py-0.5 text-[10px] text-foreground-700">
                {index + 1} / {gallery.images.length} · 演示图集
              </span>

              <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-background-950/70 p-1">
                <button
                  type="button"
                  onClick={() => setZoom((prev) => Math.max(1, Number((prev - 0.25).toFixed(2))))}
                  className="flex h-7 w-7 items-center justify-center rounded text-foreground-700 transition-colors hover:text-accent-500 cursor-pointer"
                  aria-label="缩小"
                >
                  <i className="ri-zoom-out-line"></i>
                </button>
                <span className="w-10 text-center font-mono text-[10px] text-foreground-600">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  type="button"
                  onClick={() => setZoom((prev) => Math.min(2.5, Number((prev + 0.25).toFixed(2))))}
                  className="flex h-7 w-7 items-center justify-center rounded text-foreground-700 transition-colors hover:text-accent-500 cursor-pointer"
                  aria-label="放大"
                >
                  <i className="ri-zoom-in-line"></i>
                </button>
                <button
                  type="button"
                  onClick={() => setZoom(1)}
                  className="flex h-7 w-7 items-center justify-center rounded text-foreground-700 transition-colors hover:text-accent-500 cursor-pointer"
                  aria-label="恢复原始比例"
                >
                  <i className="ri-refresh-line"></i>
                </button>
              </div>
            </div>

            <div className="mt-3 flex gap-2 overflow-x-auto hide-scrollbar">
              {gallery.images.map((image, itemIndex) => (
                <button
                  key={`${gallery.id}-thumb-${itemIndex}`}
                  type="button"
                  onClick={() => {
                    setIndex(itemIndex);
                    setZoom(1);
                  }}
                  className={`h-16 w-20 shrink-0 overflow-hidden rounded border transition-colors cursor-pointer ${
                    itemIndex === index
                      ? "border-accent-500"
                      : "border-background-300 hover:border-foreground-500"
                  }`}
                  aria-label={`查看第 ${itemIndex + 1} 张`}
                >
                  <img
                    src={image}
                    alt={`${gallery.title} 缩略图 ${itemIndex + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="rounded-sm bg-accent-500/15 px-2 py-0.5 text-accent-500">
                {gallery.theme}
              </span>
              <span className="text-foreground-500">{formatDate(gallery.uploadDate)} 上传</span>
              <span className="text-foreground-500">· {gallery.photoCount} 张演示图片</span>
            </div>
            <h1 className="mt-3 font-heading text-2xl text-foreground-950 md:text-[30px]">
              {gallery.title}
            </h1>
            <p className="mt-3 max-w-2xl text-[13px] leading-[1.9] text-foreground-500">
              {gallery.description}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center gap-2 rounded-md px-4 py-2 text-xs transition-colors whitespace-nowrap cursor-pointer ${
                  favorited
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-500 hover:text-accent-500"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                {favorited ? "已收藏" : "收藏图集"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
              <span className="text-[11px] text-foreground-500">
                提示：点击图片两侧箭头或缩略图可切换
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {gallery.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-500 transition-colors hover:text-accent-500"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <aside className="min-w-0 space-y-6">
          <section className="panel p-4">
            <h2 className="text-sm font-medium text-foreground-950">图集信息</h2>
            <dl className="mt-3 space-y-2.5 text-xs">
              {[
                { label: "摄影师", value: gallery.photographer },
                { label: "主题", value: gallery.theme },
                { label: "上传时间", value: formatDate(gallery.uploadDate) },
                { label: "图片数量", value: `${gallery.photoCount} 张` },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">{row.label}</dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 rounded-md bg-background-50 p-2.5 text-[10.5px] leading-relaxed text-foreground-500">
              摄影师与拍摄信息均为演示数据，图片为平台生成的艺术示意图。
            </p>
          </section>

          <section className="panel p-4">
            <h2 className="text-sm font-medium text-foreground-950">相关图集</h2>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {recommended.slice(0, 2).map((item) => (
                <GalleryCard key={item.id} gallery={item} />
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-primary-800/60 bg-primary-950/40 p-4">
            <h2 className="text-sm font-medium text-foreground-950">浏览提示</h2>
            <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
              所有图集仅作界面与排版演示，不包含露骨内容。请在所有设备上注意隐私与适龄设置。
            </p>
          </section>
        </aside>
      </div>

      <section className="mt-12">
        <div className="mb-4 flex items-end justify-between">
          <h2 className="text-lg text-foreground-950">推荐图集</h2>
          <Link
            to="/image-category"
            className="text-[11px] text-foreground-500 transition-colors hover:text-accent-500"
          >
            更多图集
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {recommended.map((item) => (
            <GalleryCard key={item.id} gallery={item} />
          ))}
        </div>
      </section>

      {notice ? <ActionNotice message={notice} onClose={() => setNotice("")} /> : null}
    </div>
  );
}